import React, { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';

import { SubjectAttributeKey } from '@/analytics/constants';
import { EmptySummary } from '@/common/empty-summary';
import { OrgSelectorDropdown } from '@/common/org-selector-dropdown';
import { RESERVED_ORG_IDS, ReservedOrgIds, useFastshiftDispatch } from '@/state';
import { useFastshiftSelector, setSelectedDomainOrg } from '@/state';
import { CustomerDomainOrg } from '@/state/types';

import { useSummary, useDashboardUIAnalytics } from '../hooks';

import { messages } from './messages';
import { NoOrgDisplay } from './no-org-display';
import { OtherOrgsDisplay } from './other-orgs-display';
import { SummarySectionCard } from './summary-section-card';

const topItemsStyles = xcss({
	height: '32px', // This prevents layout shift when buttons are hidden
});

const SummarySectionDetails: React.FC = () => {
	const intl = useIntl();

	const domainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);
	const {
		summaryData,
		errors,
		isEmpty,
		isL1AssessmentsLoading,
		isMigrationPlanTotalsLoading,
		isInstancesLoading,
		loadL1Assessments,
		loadMigrationPlanTotals,
		loadInstances,
	} = useSummary();

	useEffect(() => {
		if (domainOrg && !Object.values(RESERVED_ORG_IDS).includes(domainOrg.orgId as ReservedOrgIds)) {
			loadL1Assessments(domainOrg.orgId);
			loadMigrationPlanTotals(domainOrg.orgId);
			loadInstances(domainOrg.orgId);
		}
	}, [domainOrg, loadL1Assessments, loadMigrationPlanTotals, loadInstances]);

	const isNoOrgSelected = domainOrg && domainOrg.orgId === RESERVED_ORG_IDS.NO_ORG;
	const isOtherOrgsSelected = domainOrg && domainOrg.orgId === RESERVED_ORG_IDS.OTHER_ORGS;

	// Show empty state if "Other orgs" is selected
	if (isNoOrgSelected) {
		return <NoOrgDisplay />;
	}

	if (isOtherOrgsSelected) {
		return <OtherOrgsDisplay />;
	}

	if (isEmpty) {
		return <EmptySummary orgId={domainOrg?.orgId || ''} />;
	}

	return (
		<>
			<Inline space="space.200">
				<SummarySectionCard
					title={intl.formatMessage(messages.dataCenterCardHeading)}
					value={summaryData.dataCenter.count}
					isLoading={isL1AssessmentsLoading}
					error={errors.l1Assessments}
					retry={() => loadL1Assessments(domainOrg?.orgId || '')}
				/>
				<SummarySectionCard
					title={intl.formatMessage(messages.areaImprovementCardHeading)}
					value={summaryData.areaImprovement.count}
					isLoading={isL1AssessmentsLoading}
					error={errors.l1Assessments}
					retry={() => loadL1Assessments(domainOrg?.orgId || '')}
				/>
				<SummarySectionCard
					title={intl.formatMessage(messages.deletedAppsCardHeading)}
					value={summaryData.deletedApps.count}
					isLoading={isInstancesLoading}
					error={errors.instances}
					retry={() => loadInstances(domainOrg?.orgId || '')}
				/>
			</Inline>
			<Inline space="space.200">
				<SummarySectionCard
					title={intl.formatMessage(messages.migrationCompletedCardHeading)}
					value={summaryData.migrationCompleted.count}
					isLoading={isMigrationPlanTotalsLoading}
					error={errors.migrationPlanTotals}
					retry={() => loadMigrationPlanTotals(domainOrg?.orgId || '')}
				/>
				<SummarySectionCard
					title={intl.formatMessage(messages.migrationInProgressCardHeading)}
					value={summaryData.migrationInProgress.count}
					isLoading={isMigrationPlanTotalsLoading}
					error={errors.migrationPlanTotals}
					retry={() => loadMigrationPlanTotals(domainOrg?.orgId || '')}
				/>
				<SummarySectionCard
					title={intl.formatMessage(messages.unsuccessfulMigrationCardHeading)}
					value={summaryData.unsuccessfulMigration.count}
					isLoading={isMigrationPlanTotalsLoading}
					error={errors.migrationPlanTotals}
					retry={() => loadMigrationPlanTotals(domainOrg?.orgId || '')}
				/>
			</Inline>
		</>
	);
};

export const SummarySection: React.FC = () => {
	const intl = useIntl();
	const dispatch = useFastshiftDispatch();
	const customerDomain = useFastshiftSelector((state) => state.customerDomain);
	const domainOrg = useFastshiftSelector((state) => state.selectedDomainOrg);
	const setDomainOrg = (org: CustomerDomainOrg) => {
		if (domainOrg?.orgId === org.orgId) return;

		dispatch(setSelectedDomainOrg(org));
	};
	const {
		sendMigrationSummarySectionLinkClickUIEvent,
		sendOrgSelectorDropdownClickUIEvent,
		sendOrgSelectorDropdownItemClickUIEvent,
	} = useDashboardUIAnalytics();

	const baseUrl =
		process.env['NODE_ENV'] === 'production'
			? 'https://admin.atlassian.com'
			: 'https://admin.stg.atlassian.com';

	const migrationPlansUrl = `${baseUrl}/cloud-first-create-a-migration/${domainOrg?.orgId}`;
	const assessmentsUrl = `${baseUrl}/o/${domainOrg?.orgId}/product-insights/products`;

	// the selected org is not a special option (No org or Other orgs)
	const isValidCloudOrg =
		domainOrg && !Object.values(RESERVED_ORG_IDS).includes(domainOrg.orgId as ReservedOrgIds);

	return (
		<Box as="section">
			<Stack space="space.100">
				<Flex alignItems="center" justifyContent="space-between" xcss={topItemsStyles}>
					<Inline space="space.200" alignBlock="center">
						<Heading size="small" as="h2">
							<FormattedMessage {...messages.summarySectionTitle} />
						</Heading>
						<OrgSelectorDropdown
							customerDomain={customerDomain}
							selectedDomainOrg={domainOrg}
							onSetSelectedDomainOrg={setDomainOrg}
							sendOrgSelectorDropdownClickUIEvent={sendOrgSelectorDropdownClickUIEvent}
							sendOrgSelectorDropdownItemClickUIEvent={sendOrgSelectorDropdownItemClickUIEvent}
						/>
					</Inline>
					{isValidCloudOrg && (
						<Inline space="space.100">
							<LinkButton
								href={assessmentsUrl}
								iconAfter={ArrowUpRightIcon}
								target="_blank"
								onClick={() =>
									sendMigrationSummarySectionLinkClickUIEvent(
										SubjectAttributeKey.VIEW_ASSESSMENTS,
										domainOrg?.orgId || RESERVED_ORG_IDS.NO_ORG,
									)
								}
							>
								{intl.formatMessage(messages.viewAssessments)}
							</LinkButton>
							<LinkButton
								href={migrationPlansUrl}
								iconAfter={ArrowUpRightIcon}
								target="_blank"
								onClick={() =>
									sendMigrationSummarySectionLinkClickUIEvent(
										SubjectAttributeKey.VIEW_MIGRATIONS,
										domainOrg?.orgId || RESERVED_ORG_IDS.NO_ORG,
									)
								}
							>
								{intl.formatMessage(messages.viewMigrations)}
							</LinkButton>
						</Inline>
					)}
				</Flex>
				<SummarySectionDetails />
			</Stack>
		</Box>
	);
};
